import React, { useCallback, useEffect, useState } from "react";
import { fetchEndOfDay } from "../services/api";
import { formatPrice } from "../utils/currency";

// "Z report" close-out over a flexible window (date + hours), so a manager
// can close a single shift, not just a calendar day. Times are Ethiopia
// local (UTC+3). Payment/cash reconciliation is omitted until payments exist.
const MS = 1000;
const ET_MS = 3 * 3600 * MS;

// Ethiopia local "YYYY-MM-DDTHH:MM" for a given epoch ms.
function localStr(ms) {
  return new Date(ms + ET_MS).toISOString().slice(0, 16);
}
function startOfLocalDay(ms) {
  return localStr(ms).slice(0, 10) + "T00:00";
}

function Section({ title, badge, children }) {
  return (
    <section style={{ marginTop: 16 }}>
      <h3 style={{ margin: "0 0 6px", fontSize: 13, textTransform: "uppercase", letterSpacing: 1, color: "#008080", borderBottom: "2px solid #eef2f6", paddingBottom: 4 }}>
        {title}{badge ? <span style={{ marginLeft: 8, fontSize: 10, color: "#8a6d00", background: "#fdf3d6", border: "1px solid #f0d98b", borderRadius: 10, padding: "1px 7px" }}>{badge}</span> : null}
      </h3>
      {children}
    </section>
  );
}
function Row({ label, value, sub }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, padding: "3px 0" }}>
      <span>{label}{sub ? <span className="muted" style={{ fontSize: 12 }}> · {sub}</span> : null}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );
}

export default function EndOfDayPage() {
  const now = Date.now();
  const [start, setStart] = useState(startOfLocalDay(now)); // today 00:00
  const [end, setEnd] = useState(localStr(now));            // now
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      setData(await fetchEndOfDay({ start, end }));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load the report.");
    } finally { setLoading(false); }
  }, [start, end]);
  useEffect(() => { load(); }, [load]);

  const preset = (which) => {
    const n = Date.now();
    if (which === "today") { setStart(startOfLocalDay(n)); setEnd(localStr(n)); }
    else if (which === "yesterday") {
      const y = n - 24 * 3600 * MS;
      setStart(startOfLocalDay(y)); setEnd(startOfLocalDay(n));
    } else if (which === "24h") { setStart(localStr(n - 24 * 3600 * MS)); setEnd(localStr(n)); }
  };

  const cur = (data && data.currency) || "ETB";
  const money = (v) => formatPrice(v || 0, cur);
  const periodText = data ? `${data.range.from_local.replace("T", " ")} → ${data.range.to_local.replace("T", " ")}` : "";

  const printReport = () => {
    if (!data) return;
    const w = window.open("", "_blank");
    if (!w) return;
    const line = (l, v) => `<tr><td>${l}</td><td style="text-align:right;font-weight:600">${v}</td></tr>`;
    const block = (title, rows) =>
      `<h3 style="color:#008080;border-bottom:1px solid #ccc;font-size:12px;text-transform:uppercase;margin:14px 0 4px">${title}</h3><table style="width:100%;border-collapse:collapse">${rows}</table>`;
    const s = data.summary;
    let html = `<h2 style="margin:0">${data.restaurant_name || ""}</h2>`
      + `<div style="color:#666;font-size:13px;margin-bottom:8px">END OF DAY (F&B SUMMARY) · ${periodText} · ${cur}</div>`;
    html += block("Sales summary",
      line("Orders", s.orders) + line("Gross sales", money(s.gross)) + line("Average order", money(s.aov))
      + line("Cancelled", `${s.cancelled_orders} (${money(s.cancelled_value)})`) + line("Cancellation rate", `${s.cancellation_rate}%`));
    html += block("By category", data.by_category.map((r) => line(r.name, money(r.revenue))).join(""));
    html += block("By station", data.by_station.map((r) => line(r.name, money(r.revenue))).join(""));
    html += block("Top items", data.top_items.map((r) => line(`${r.name} ×${r.qty}`, money(r.revenue))).join(""));
    html += block("By channel", data.by_channel.map((r) => line(`${r.label} (${r.orders})`, money(r.revenue))).join(""));
    html += block("By daypart", data.by_daypart.map((r) => line(r.key, money(r.revenue))).join(""));
    html += block("By captain", data.by_staff.map((r) => line(`${r.name} (${r.orders})`, money(r.revenue))).join(""));
    if (data.payments && data.payments.by_method.length) {
      html += block("Payments recorded",
        data.payments.by_method.map((r) => line(`${r.label} (${r.count})`, money(r.total))).join("")
        + line("<b>Total recorded</b>", `<b>${money(data.payments.total)}</b>`));
    }
    html += block("Service & quality",
      line("Avg cook time", data.service.cook_median_min == null ? "—" : `${data.service.cook_median_min} min`)
      + line("Avg total wait", data.service.wait_median_min == null ? "—" : `${data.service.wait_median_min} min`)
      + line("Rating", data.ratings.count ? `Food ${data.ratings.food ?? "—"} · Service ${data.ratings.service ?? "—"} (${data.ratings.count})` : "—"));
    w.document.write(`<html><head><title>End of Day ${periodText}</title></head><body style="font-family:Arial;max-width:460px;margin:0 auto;padding:16px">${html}`
      + `<scr`+`ipt>window.onload=function(){window.print()}<\/scr`+`ipt></body></html>`);
    w.document.close();
  };

  return (
    <>
      <h1 style={{ margin: 0 }}>🧾 End of Day <span className="muted" style={{ fontSize: 14 }}>(F&B summary)</span></h1>

      <div className="card" style={{ marginTop: 10, padding: 12, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>
          From<br />
          <input type="datetime-local" value={start} max={end} onChange={(e) => setStart(e.target.value)} />
        </label>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>
          To<br />
          <input type="datetime-local" value={end} min={start} onChange={(e) => setEnd(e.target.value)} />
        </label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button className="btn secondary small" onClick={() => preset("today")}>Today</button>
          <button className="btn secondary small" onClick={() => preset("yesterday")}>Yesterday</button>
          <button className="btn secondary small" onClick={() => preset("24h")}>Last 24h</button>
        </div>
        <span style={{ flex: 1 }} />
        <button className="btn secondary small" onClick={printReport} disabled={!data}>Print</button>
      </div>

      {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}
      {loading ? <p className="muted" style={{ marginTop: 12 }}>Loading…</p> : data ? (
        <div className="card" style={{ marginTop: 12, maxWidth: 560 }}>
          <div className="muted" style={{ fontSize: 13 }}>{data.restaurant_name} · {periodText} · {cur}</div>

          <Section title="Sales summary">
            <Row label="Orders" value={data.summary.orders} />
            <Row label="Gross sales" value={money(data.summary.gross)} />
            <Row label="Average order" value={money(data.summary.aov)} />
            <Row label="Cancelled" value={`${data.summary.cancelled_orders} · ${money(data.summary.cancelled_value)}`} />
            <Row label="Cancellation rate" value={`${data.summary.cancellation_rate}%`} />
          </Section>

          {data.by_category.length > 0 && (
            <Section title="By category">
              {data.by_category.map((r) => <Row key={r.name} label={r.name} value={money(r.revenue)} />)}
            </Section>
          )}
          {data.by_station.length > 0 && (
            <Section title="By station">
              {data.by_station.map((r) => <Row key={r.name} label={r.name} value={money(r.revenue)} />)}
            </Section>
          )}
          {data.top_items.length > 0 && (
            <Section title="Top items">
              {data.top_items.map((r) => <Row key={r.name} label={r.name} sub={`×${r.qty}`} value={money(r.revenue)} />)}
            </Section>
          )}
          <Section title="By channel">
            {data.by_channel.map((r) => <Row key={r.key} label={r.label} sub={`${r.orders} orders`} value={money(r.revenue)} />)}
          </Section>
          <Section title="By daypart">
            {data.by_daypart.map((r) => <Row key={r.key} label={r.key} value={money(r.revenue)} />)}
          </Section>
          {data.by_staff.length > 0 && (
            <Section title="By captain">
              {data.by_staff.map((r) => <Row key={r.name} label={r.name} sub={`${r.orders} orders`} value={money(r.revenue)} />)}
            </Section>
          )}
          {data.payments && data.payments.by_method.length > 0 && (
            <Section title="Payments recorded">
              {data.payments.by_method.map((r) => (
                <Row key={r.method} label={r.label} sub={`${r.count} ${r.count === 1 ? "payment" : "payments"}`} value={money(r.total)} />
              ))}
              <Row label="Total recorded" value={money(data.payments.total)} />
            </Section>
          )}

          <Section title="Service & quality" badge="ODRA exclusive">
            <Row label="Avg cook time" value={data.service.cook_median_min == null ? "—" : `${data.service.cook_median_min} min`} />
            <Row label="Avg total wait" value={data.service.wait_median_min == null ? "—" : `${data.service.wait_median_min} min`} />
            <Row label="Rating" value={data.ratings.count ? `${data.ratings.food ?? "—"}★ food · ${data.ratings.service ?? "—"}★ service (${data.ratings.count})` : "—"} />
          </Section>

          <p className="muted" style={{ fontSize: 12, marginTop: 16, borderTop: "1px dashed #e5e7eb", paddingTop: 10 }}>
            Payments shown are those staff recorded at tab close (Telebirr / CBE Birr / cash). A VAT line needs a tax rate configured.
          </p>
        </div>
      ) : null}
    </>
  );
}
